import { useCallback, useSyncExternalStore } from 'react';

/**
 * Whether a media query matches, kept current as the viewport changes. Pass one of the `mq`
 * conditions from `./breakpoints` — never a raw width (FS-3 AC-6). A layout that must change at a
 * breakpoint sets a `data-*` attribute from this, because the `bp/*` values cannot appear in a CSS
 * `@media` that reads a custom property (design-system §9).
 *
 * `useSyncExternalStore` rather than an effect and state: the match is external browser state, so it
 * is read on the first render with no extra one, and re-read on the query list's `change` event.
 * Without `window.matchMedia` it answers `true`: every `mq` condition is a `min-width`, and the
 * app is client-rendered only (AD #21), so the widest layout is the safe default — the server
 * snapshot answers the same.
 */
export function useMediaQuery(query: string): boolean {
    const subscribe = useCallback(
        (onChange: () => void) => {
            if (typeof window.matchMedia !== 'function') {
                return () => {};
            }
            const list = window.matchMedia(query);
            list.addEventListener('change', onChange);
            return () => list.removeEventListener('change', onChange);
        },
        [query],
    );
    const matches = useCallback(
        () => typeof window.matchMedia !== 'function' || window.matchMedia(query).matches,
        [query],
    );
    return useSyncExternalStore(subscribe, matches, () => true);
}
